import { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Camera, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/features/auth/AuthContext';
import { APP_NAME, visibleSections, type NavSection } from './navigation';

/**
 * Primary navigation. Rendered inline on desktop and inside a drawer on mobile —
 * `onNavigate` lets the mobile host close itself when a destination is chosen.
 *
 * ── Collapsible sections ────────────────────────────────────────────────────────────────
 * Every section with more than one destination folds away; a section with a single
 * destination, or one marked `flat` (Dashboard and Quick Booking), shows its links with no
 * heading and nothing to fold. Three rules make the collapsing safe rather than annoying:
 *
 *   1. The section holding the current page is **always open**, whatever the stored state.
 *      A collapsed sidebar that hides where you are leaves no way to tell which screen you
 *      are on, which is worse than the space it saves.
 *   2. The choice persists in `localStorage`, not `sessionStorage` — this is a working
 *      preference about how somebody likes their tools laid out, not a per-visit decision.
 *   3. Only collapsed sections are stored. A new section added later therefore starts open,
 *      rather than being silently hidden from everyone who has used the app before.
 *
 * ── Compact mode ────────────────────────────────────────────────────────────────────────
 * When the desktop sidebar is minimised, `compact` shows icons only. Each section heading
 * becomes a circle holding its first and last letter ('Billing' → BG), and still folds its
 * section — sharing the same stored state, so a section folded in one mode is folded in the
 * other. The full heading stays as the tooltip and, visually hidden, as the button's name;
 * each icon likewise carries its label as a tooltip and accessible name.
 */

const STORAGE_KEY = 'nav-collapsed-sections';

/** The application name split for the two-tone wordmark: 'Event' + 'Media'. */
const WORDMARK = {
  lead: APP_NAME.slice(0, APP_NAME.lastIndexOf(' ')),
  tail: APP_NAME.slice(APP_NAME.lastIndexOf(' ') + 1),
};

/** 'Billing' → 'BG': the first and last letter, for the minimised rail. */
function monogram(heading: string): string {
  const letters = heading.replace(/[^A-Za-z]/g, '');
  return (letters.length > 1 ? letters[0]! + letters[letters.length - 1]! : letters).toUpperCase();
}

function readCollapsed(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    // Guarded: a hand-edited or half-written value must not take the whole shell down.
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : [];
  } catch {
    return [];
  }
}

export function Sidebar({
  onNavigate,
  compact = false,
}: {
  onNavigate?: () => void;
  compact?: boolean;
}) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const sections = visibleSections(user?.role);

  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
    } catch {
      // A full or blocked storage quota is not a reason to stop rendering the navigation.
    }
  }, [collapsed]);

  /** Does this section contain the page currently on screen? */
  const holdsCurrentPage = useCallback(
    (section: NavSection) =>
      section.items.some((item) =>
        item.matchPrefix ? pathname.startsWith(item.to) : pathname === item.to,
      ),
    [pathname],
  );

  const toggle = (heading: string) =>
    setCollapsed((current) =>
      current.includes(heading)
        ? current.filter((entry) => entry !== heading)
        : [...current, heading],
    );

  return (
    <nav
      aria-label="Main navigation"
      className={cn(
        'flex h-full flex-col overflow-y-auto overflow-x-hidden',
        compact ? 'gap-3 p-2' : 'gap-4 p-4',
      )}
    >
      <div className={cn('flex items-center gap-2', compact ? 'justify-center' : 'px-2')}>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-600 text-white">
          <Camera className="h-4 w-4" aria-hidden="true" />
        </span>
        {/* theme.md wordmark: white, with the gold reserved for its second half. */}
        <span
          className={cn('min-w-0 text-sm font-bold leading-tight text-white', compact && 'sr-only')}
        >
          {WORDMARK.lead} <span className="text-brand-gold">{WORDMARK.tail}</span>
        </span>
      </div>

      {sections.map((section) => {
        const single = section.flat === true || section.items.length === 1;
        const open = single || holdsCurrentPage(section) || !collapsed.includes(section.heading);
        const panelId = `nav-${section.heading.toLowerCase().replace(/\s+/g, '-')}`;

        const list = (
          <ul id={panelId} className={cn('space-y-0.5', single ? '' : 'mt-1')} hidden={!open}>
            {section.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={!item.matchPrefix}
                  onClick={onNavigate}
                  title={compact ? item.label : undefined}
                  aria-label={compact ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                      compact && 'justify-center px-0 py-2.5',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                      isActive
                        ? 'bg-brand-600 text-white'
                        : 'text-sidebar-text hover:bg-white/[.08] hover:text-white',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        className={cn('h-4 w-4 shrink-0', isActive ? 'text-white' : 'text-current')}
                        aria-hidden="true"
                      />
                      {!compact && <span className="truncate">{item.label}</span>}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        );

        // A section with one destination is its own heading; a disclosure around a single
        // link is a control that hides one row, which is not worth a keyboard stop. A flat
        // section's links are used constantly, so they are never hidden behind one either.
        if (single) {
          return <div key={section.heading}>{list}</div>;
        }

        return (
          <div key={section.heading}>
            <h2>
              <button
                type="button"
                onClick={() => toggle(section.heading)}
                aria-expanded={open}
                aria-controls={panelId}
                title={compact ? section.heading : undefined}
                className={cn(
                  'flex w-full items-center rounded-md py-1.5',
                  'micro-label text-sidebar-legend',
                  compact ? 'justify-center gap-0.5 px-1' : 'justify-between gap-2 px-2',
                  'transition-colors hover:bg-white/[.08] hover:text-white',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                )}
              >
                {compact ? (
                  <>
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/20 text-[0.625rem] tracking-normal"
                      aria-hidden="true"
                    >
                      {monogram(section.heading)}
                    </span>
                    <span className="sr-only">{section.heading}</span>
                  </>
                ) : (
                  <span className="truncate">{section.heading}</span>
                )}
                <ChevronDown
                  className={cn(
                    'shrink-0 transition-transform',
                    compact ? 'h-3 w-3' : 'h-4 w-4',
                    open ? '' : '-rotate-90',
                  )}
                  aria-hidden="true"
                />
              </button>
            </h2>
            {list}
          </div>
        );
      })}
    </nav>
  );
}
